// @section 1 v5 Accounts — Pipeline Flow.
// Wired to /api/hq/founder-os/{pipeline,stall-banner,cold-backlog,account/:id}
// + PUT /deals/:id/stage for kanban transitions. Empty/loading states render
// in-place; failures surface as a banner.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FoAccountRecord,
  FoCard,
  FoColdItem,
  FoPipelineStage,
  FoStallItem,
  foGetAccount,
  foGetColdBacklog,
  foGetPipeline,
  foGetStallBanner,
  foPutDealStage,
} from '../api';
import { useIsMobile } from '../lib/useIsMobile';
import { FeedScroll, FeedHeader, FeedPills, FeedEmpty, FeedCard } from '../components/MobileFeed';

const FALLBACK_STAGES = ['lead', 'qualified', 'proposal', 'negotiation', 'won'] as const;
const FALLBACK_LABEL: Record<string, string> = {
  lead: 'Lead', qualified: 'Qualified', proposal: 'Proposal', negotiation: 'Negotiation', won: 'Won',
};

function formatDeal(c: FoCard): string {
  if (c.deal_cents == null) return 'tbd';
  const v = c.deal_cents / 100;
  if (v >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${v.toFixed(0)}`;
}

export default function AccountsV5() {
  const [stages, setStages] = useState<FoPipelineStage[] | null>(null);
  const [stalled, setStalled] = useState<FoStallItem[]>([]);
  const [cold, setCold] = useState<FoColdItem[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [record, setRecord] = useState<FoAccountRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);
  const navigate = useNavigate();
  // @block 1.0 Phone fork: the swipe-sideways kanban is the wrong shape for a
  // phone. On mobile we render a single heat/urgency-sorted feed instead.
  const isMobile = useIsMobile();

  // @block 1.1 Initial fetch — pipeline / stall / cold in parallel
  useEffect(() => {
    let cancelled = false;
    Promise.all([foGetPipeline(), foGetStallBanner(), foGetColdBacklog()])
      .then(([p, s, c]) => {
        if (cancelled) return;
        setStages(p);
        setStalled(s);
        setCold(c);
        // Auto-select the first card with the highest heat across all stages.
        const all = p.flatMap(x => x.cards);
        const top = all.sort((a, b) => Number(b.heat) - Number(a.heat))[0];
        if (top?.company_id) setSelectedCompanyId(top.company_id);
      })
      .catch(e => !cancelled && setError(String(e)));
    return () => { cancelled = true; };
  }, []);

  // @block 1.2 Right-rail record — refetch on selection
  useEffect(() => {
    if (!selectedCompanyId) { setRecord(null); return; }
    let cancelled = false;
    foGetAccount(selectedCompanyId)
      .then(r => !cancelled && setRecord(r))
      .catch(e => !cancelled && setError(String(e)));
    return () => { cancelled = true; };
  }, [selectedCompanyId]);

  const counts = useMemo(() => {
    if (!stages) return { total: 0, engaged: 0, warm: 0, stalled: 0 };
    const all = stages.flatMap(s => s.cards);
    return {
      total:   all.length,
      engaged: all.filter(c => c.warmth === 'engaged').length,
      warm:    all.filter(c => c.warmth === 'warm').length,
      stalled: all.filter(c => c.warmth === 'stall').length,
    };
  }, [stages]);

  async function moveCard(dealId: string, toStage: string) {
    if (!stages) return;
    const prev = stages;
    // Optimistic update
    setStages(stages.map(stg => ({
      ...stg,
      cards: stg.key === toStage
        ? [...stg.cards, ...prev.flatMap(s => s.cards.filter(c => c.deal_id === dealId)).map(c => ({ ...c, stage: toStage as FoCard['stage'] }))]
        : stg.cards.filter(c => c.deal_id !== dealId),
      count: stg.key === toStage
        ? stg.count + 1
        : stg.cards.some(c => c.deal_id === dealId) ? Math.max(0, stg.count - 1) : stg.count,
    })));
    try {
      await foPutDealStage(dealId, toStage);
    } catch (e) {
      setError(String(e));
      setStages(prev); // rollback
    }
  }

  if (error) {
    return <div className="v5-page" style={{ padding: 24, color: 'var(--neg)' }}>API error: {error}</div>;
  }
  if (!stages) {
    return <div className="v5-page" style={{ padding: 24, color: 'var(--tx-3)' }}>Loading pipeline…</div>;
  }

  if (isMobile) {
    return <AccountsMobileFeed stages={stages} counts={counts} navigate={navigate} />;
  }

  return (
    <div className="v5-page page-accounts">
      <div className="accts-page-body">
        <div className="page-head">
          <div className="crumb"><span>Atlas</span><span className="sep">›</span><b>Accounts</b></div>
          <div className="page-title">
            <h1>Pipeline Flow</h1>
            <div className="sub">
              {counts.total} accounts · {counts.engaged} engaged · {counts.warm} warm
              {stalled.length > 0 ? ` · ${stalled.length} stalled` : ''}
              {cold.length > 0 ? ` · ${cold.length} cold backlog` : ''}
            </div>
          </div>
          <div className="toolbar">
            <div className="pill-group">
              <button className="pill active">All<span className="ct">{counts.total}</span></button>
              <button className="pill">Mine</button>
              <button className="pill">Patrick</button>
            </div>
            <div className="pill-group">
              <button className="pill active">By stage</button>
              <button className="pill">By warmth</button>
              <button className="pill">By value</button>
            </div>
            <div className="search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
              </svg>
              <input placeholder="Search accounts…" />
              <kbd>/</kbd>
            </div>
          </div>
        </div>

        {stalled.length > 0 && (
          <div className="stall-banner">
            <span className="lbl">▼ Stalled</span>
            <span className="text">{stalled.length} accounts went quiet in the last 14+ days</span>
            <div className="stalled-list">
              {stalled.slice(0, 6).map(s => (
                <span className="chip" key={s.company_id} onClick={() => setSelectedCompanyId(s.company_id)}>
                  {s.badge}{s.days_quiet != null ? ` · ${s.days_quiet}d` : ''}
                </span>
              ))}
            </div>
            <button className="review-btn">Review now →</button>
          </div>
        )}

        <div className="pipeline-flow">
          {FALLBACK_STAGES.map(stageKey => {
            const stage = stages.find(s => s.key === stageKey)
              ?? { key: stageKey, label: FALLBACK_LABEL[stageKey], count: 0, cards: [] };
            return (
              <div
                className="stage-col"
                key={stage.key}
                onDragOver={e => { e.preventDefault(); }}
                onDrop={e => {
                  e.preventDefault();
                  if (draggedDealId) {
                    void moveCard(draggedDealId, stage.key);
                    setDraggedDealId(null);
                  }
                }}
              >
                <div className="stage-head">
                  <span className="name"><span className={`indicator ${stage.key}`}></span>{stage.label}</span>
                  <span className="count">{stage.count}</span>
                </div>
                <div className="stage-col-body">
                  {stage.cards.map(card => (
                    <div
                      key={card.deal_id}
                      className={`acct-card ${card.warmth}${selectedCompanyId === card.company_id ? ' selected' : ''}`}
                      draggable
                      onDragStart={() => setDraggedDealId(card.deal_id)}
                      onDragEnd={() => setDraggedDealId(null)}
                      onClick={() => navigate(`/opportunity/${card.deal_id}`)}
                    >
                      <div className="row1">
                        <div className="name">{card.name}</div>
                        <div className="heat">{Number(card.heat).toFixed(2)}</div>
                      </div>
                      {card.meta && <div className="meta">{card.meta}</div>}
                      <div className="foot">
                        <div className="deal">{formatDeal(card)}</div>
                        <div className="decay">
                          <span className={`decay-bar ${card.decay_band}`}><div /></span>
                          {card.age_label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {cold.length > 0 && (
          <div className="cold-backlog">
            <span className="lbl">Cold backlog</span>
            <div className="chips">
              {cold.slice(0, 3).map(c => (
                <span className="ck" key={c.company_id} onClick={() => setSelectedCompanyId(c.company_id)}>
                  ○ {c.name}
                </span>
              ))}
            </div>
            {cold.length > 3 && (
              <span className="more">Show all {cold.length} cold accounts →</span>
            )}
          </div>
        )}
      </div>

      <RightRail record={record} />
    </div>
  );
}

// @section 2 Mobile feed — heat/urgency-sorted single column for phones.
// Replaces the swipe-sideways kanban: one prioritized list, stage as a chip,
// stalled cards float to the top, tap a card → the opportunity detail.
const WARMTH_META: Record<string, { label: string; color: string }> = {
  engaged: { label: 'hot',     color: 'var(--pos)' },
  warm:    { label: 'warm',    color: 'var(--brand-4)' },
  stall:   { label: 'stalled', color: 'var(--neg)' },
  cold:    { label: 'cold',    color: 'var(--tx-4)' },
};
function warmthMeta(w: string) {
  return WARMTH_META[w] ?? { label: 'aging', color: 'var(--warn)' };
}
function fmtK(cents: number): string {
  const v = cents / 100;
  return v >= 1000 ? `$${Math.round(v / 1000)}K` : `$${Math.round(v)}`;
}

type FeedFilter = 'all' | 'hot' | 'stalled' | 'won';

function AccountsMobileFeed({
  stages, counts, navigate,
}: {
  stages: FoPipelineStage[];
  counts: { total: number; engaged: number; warm: number; stalled: number };
  navigate: (to: string) => void;
}) {
  const [filter, setFilter] = useState<FeedFilter>('all');

  const allCards = useMemo(
    () => stages.flatMap((s) => s.cards.map((c) => ({ card: c, stageLabel: s.label }))),
    [stages],
  );
  const openValueCents = useMemo(
    () => allCards
      .filter(({ card }) => card.stage !== 'won')
      .reduce((a, { card }) => a + (card.deal_cents ?? 0), 0),
    [allCards],
  );
  const wonCount = useMemo(() => allCards.filter(({ card }) => card.stage === 'won').length, [allCards]);
  const hot = counts.engaged + counts.warm;

  // Heat/urgency order: stalled deals float to the top (they need rescue),
  // then everything by heat descending.
  const feed = useMemo(() => {
    const keep = ({ card }: { card: FoCard }) =>
      filter === 'all' ? true
      : filter === 'hot' ? (card.warmth === 'engaged' || card.warmth === 'warm')
      : filter === 'stalled' ? card.warmth === 'stall'
      : card.stage === 'won';
    const rank = (c: FoCard) => (c.warmth === 'stall' ? 0 : 1);
    return allCards
      .filter(keep)
      .sort((a, b) => rank(a.card) - rank(b.card) || Number(b.card.heat) - Number(a.card.heat));
  }, [allCards, filter]);

  const pills: { key: FeedFilter; label: string; ct: number }[] = [
    { key: 'all',     label: 'All',     ct: counts.total },
    { key: 'hot',     label: 'Hot',     ct: hot },
    { key: 'stalled', label: 'Stalled', ct: counts.stalled },
    { key: 'won',     label: 'Won',     ct: wonCount },
  ];

  return (
    <FeedScroll>
      <FeedHeader
        title="Pipeline"
        summary={`${fmtK(openValueCents)} open · ${counts.total} deals · ${hot} hot${counts.stalled > 0 ? ` · ${counts.stalled} stalled` : ''}`}
      />
      <FeedPills options={pills} value={filter} onChange={setFilter} />
      {feed.length === 0 ? (
        <FeedEmpty>No accounts in this view.</FeedEmpty>
      ) : (
        feed.map(({ card, stageLabel }) => {
          const wm = warmthMeta(card.warmth);
          return (
            <FeedCard
              key={card.deal_id}
              title={card.name}
              value={formatDeal(card)}
              chip={stageLabel}
              status={{ label: `${wm.label}${card.age_label ? ` · ${card.age_label}` : ''}`, color: wm.color }}
              trailing={`heat ${Number(card.heat).toFixed(2)}`}
              meta={card.meta ?? undefined}
              onClick={() => navigate(`/opportunity/${card.deal_id}`)}
            />
          );
        })
      )}
    </FeedScroll>
  );
}

function RightRail({ record }: { record: FoAccountRecord | null }) {
  if (!record) {
    return <div className="right-rail" style={{ color: 'var(--tx-4)' }}>Select an account.</div>;
  }
  const c = record.company;
  const d = record.deal;
  return (
    <div className="right-rail">
      <div className="record">
        <div className="record-top">
          <div className="record-avatar">{c.badge}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2>{c.name}</h2>
            <div className="record-meta">
              {c.domain && (<><a href={`https://${c.domain}`} target="_blank" rel="noreferrer">{c.domain}</a>{' · '}</>)}
              {c.industry ?? '—'}
              {c.last_touched_at && (<> · Last touch {new Date(c.last_touched_at).toLocaleDateString()}</>)}
            </div>
          </div>
        </div>

        <div className="path">
          {record.path.map(s => (
            <div key={s.key} className={`stage${s.kind === 'done' ? ' done' : s.kind === 'current' ? ' current' : ''}`}>
              {s.label}
            </div>
          ))}
        </div>

        <div className="record-stats">
          <div className="record-stat">
            <div className="lbl">Deal</div>
            <div className="val brand">{d?.value_cents != null ? `$${Math.round(d.value_cents / 100000)}K` : (d ? '—' : '—')}</div>
          </div>
          <div className="record-stat">
            <div className="lbl">Heat</div>
            <div className="val">{d ? Number(d.heat).toFixed(2) : '—'}</div>
          </div>
          <div className="record-stat">
            <div className="lbl">Momentum</div>
            <div className="val">{d?.momentum_pct != null ? `${d.momentum_pct}%` : '—'}</div>
          </div>
        </div>

        <div className="record-actions">
          <button className="btn primary">▶ Open Workspace</button>
          <button className="btn">+ Offer</button>
          <button className="btn">Book Mtg</button>
        </div>
      </div>

      {record.bd_queue.length > 0 && (
        <div className="related">
          <div className="related-head">
            <h3>BD queue · this account</h3>
            <span className="ct">{record.bd_queue.length} active</span>
          </div>
          {record.bd_queue.map(q => (
            <div className="queue-row" key={q.id}>
              <span className={`stage-pill ${q.stage}`}>{q.stage}</span>
              <span style={{ fontWeight: 500 }}>{q.title}</span>
              <span className="pct">{q.progress_pct}%</span>
            </div>
          ))}
        </div>
      )}

      {record.contacts.length > 0 && (
        <div className="related">
          <div className="related-head"><h3>Contacts</h3><span className="add">+ Add</span></div>
          {record.contacts.slice(0, 6).map(p => (
            <div className="related-row" key={p.id}>
              <div>
                <div className="label">{p.full_name || '(no name)'}</div>
                <div className="meta">{[p.title, p.email].filter(Boolean).join(' · ')}</div>
              </div>
              {p.last_touched_at && (
                <span className="v">{new Date(p.last_touched_at).toLocaleDateString()}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {record.activities.length > 0 && (
        <div className="related">
          <div className="related-head"><h3>Activity · last 14d</h3><span className="ct">{record.activities.length}</span></div>
          {record.activities.slice(0, 6).map(a => (
            <div className="related-row" key={a.id}>
              <div>
                <div className="label">{a.subject || a.activity_type}</div>
                {a.body && <div className="meta">{a.body}</div>}
              </div>
              <span className="v">{new Date(a.occurred_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      <div className="related">
        <div className="related-head"><h3>BD rollup</h3></div>
        <div className="related-row"><div className="label">Facts captured</div><span className="v">{record.bd_rollup.facts_captured}</span></div>
        <div className="related-row"><div className="label">Open asks</div><span className="v brand">{record.bd_rollup.open_asks}</span></div>
        <div className="related-row"><div className="label">To research</div><span className="v warn">{record.bd_rollup.to_research}</span></div>
        <div className="related-row"><div className="label">Latest offer</div><span className="v">{record.bd_rollup.latest_offer ?? '—'}</span></div>
      </div>
    </div>
  );
}
